import { API_CONFIG } from "../../../config/netConfig";

export interface TokenInfo {
  id: string;
  tokenValue?: string;
  token_value?: string;
  allowedAudience?: string[];
  allowed_audience?: string[];
  expiresAt?: string;
  expires_at?: string;
  isActive?: boolean;
  is_active?: boolean;
  usageCount?: number;
  usage_count?: number;
  maxUsage?: number;
  max_usage?: number;
  lastUsedAt?: string;
  last_used_at?: string;
  ipWhitelist?: string;
  ip_whitelist?: string;
  description?: string;
}

export interface CreateTokenParams {
  target_service: string; // 必填：目标服务名称
  expires_at?: string;
  is_active?: boolean;
  max_usage?: number;
  ip_whitelist?: string;
  description?: string;
}

export interface UpdateTokenParams {
  allowed_audience?: string[];
  expires_at?: string;
  is_active?: boolean;
  max_usage?: number;
  ip_whitelist?: string;
  description?: string;
}

// API响应接口
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

// 获取认证token
const getAuthToken = (): string | null => {
  return localStorage.getItem("mslogin_token") || localStorage.getItem("token") || sessionStorage.getItem("token");
};

// 构建请求头
const getHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// 处理响应
const handleResponse = async (response: Response): Promise<ApiResponse> => {
  try {
    let responseData: any;
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      responseData = await response.json();
    } else {
      const text = await response.text();
      console.error("[TokenService] 非JSON响应:", text);
      return {
        success: false,
        message: `服务器错误 (${response.status}): ${text || response.statusText}`
      };
    }

    if (!response.ok) {
      const errorMessage = responseData?.message || responseData?.error || `HTTP Error: ${response.status}`;
      console.error("[TokenService] 请求失败:", {
        status: response.status,
        statusText: response.statusText,
        responseData,
      });
      return { success: false, message: errorMessage };
    }

    if (responseData && typeof responseData === "object" && "success" in responseData) {
      return responseData;
    }

    return { success: true, data: responseData, message: "操作成功" };
  } catch (error) {
    console.error("[TokenService] 处理响应失败:", error);
    return { success: false, message: "请求处理失败，请稍后重试" };
  }
};

/**
 * Token服务类
 * 处理所有Token相关的API调用
 */
export class TokenService {
  /**
   * 获取Token信息
   */
  static async fetchTokenInfo(serviceId: string): Promise<ApiResponse<TokenInfo>> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens`, {
        method: "GET",
        headers: getHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("🔍 [TokenService.fetchTokenInfo] 后端返回数据:", {
          result,
          tokens: result.data?.tokens,
          firstToken: result.data?.tokens?.[0],
          allowedAudience: result.data?.tokens?.[0]?.allowedAudience,
          allowed_audience: result.data?.tokens?.[0]?.allowed_audience,
        });
        if (result.success && result.data.tokens.length > 0) {
          const tokenData = result.data.tokens[0];
          console.log("🔍 [TokenService.fetchTokenInfo] 返回的token数据:", {
            tokenData,
            allowedAudience: tokenData.allowedAudience,
            allowed_audience: tokenData.allowed_audience,
          });
          return { success: true, data: tokenData };
        } else {
          return { success: true, data: undefined };
        }
      } else {
        return { success: true, data: undefined };
      }
    } catch (error) {
      console.error("获取Token信息失败:", error);
      return { success: false, message: "获取Token信息失败" };
    }
  }

  /**
   * 创建Token
   */
  static async createToken(serviceId: string, params: CreateTokenParams): Promise<ApiResponse<TokenInfo>> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens`;
      const headers = getHeaders();
      const body = JSON.stringify(params);

      console.log("[TokenService.createToken] 发送请求:", {
        url,
        method: "POST",
        headers,
        body,
        serviceId,
        params
      });

      const response = await fetch(url, {
        method: "POST",
        headers,
        body,
      });

      console.log("[TokenService.createToken] 响应状态:", response.status, response.statusText);
      console.log("[TokenService.createToken] 响应头:", Object.fromEntries(response.headers.entries()));

      return handleResponse(response);
    } catch (error) {
      console.error("[TokenService.createToken] 创建Token失败:", error);
      return { success: false, message: "创建Token失败" };
    }
  }

  /**
   * 更新Token
   */
  static async updateToken(serviceId: string, tokenId: string, params: UpdateTokenParams): Promise<ApiResponse<TokenInfo>> {
    try {
      console.log("[TokenService] 更新Token请求:", {
        serviceId,
        tokenId,
        params,
        url: `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens/${tokenId}`,
      });

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens/${tokenId}`,
        {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify(params),
        }
      );

      console.log("[TokenService] 更新Token响应状态:", response.status, response.statusText);
      const result = await handleResponse(response);
      console.log("[TokenService] 更新Token响应结果:", result);
      return result;
    } catch (error) {
      console.error("[TokenService] 更新Token失败:", error);
      return { success: false, message: "更新Token失败" };
    }
  }

  /**
   * 刷新Token
   */
  static async refreshToken(serviceId: string, tokenId: string): Promise<ApiResponse<TokenInfo>> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens/${tokenId}/refresh`,
        {
          method: "POST",
          headers: getHeaders(),
        }
      );

      return handleResponse(response);
    } catch (error) {
      console.error("刷新Token失败:", error);
      return { success: false, message: "刷新Token失败" };
    }
  }

  /**
   * 删除Token
   */
  static async deleteToken(serviceId: string, tokenId: string): Promise<ApiResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens/${tokenId}`,
        {
          method: "DELETE",
          headers: getHeaders(),
        }
      );

      return handleResponse(response);
    } catch (error) {
      console.error("删除Token失败:", error);
      return { success: false, message: "删除Token失败" };
    }
  }
}
